import { useQuery } from "@tanstack/react-query";
import { BarChart3, IndianRupee, Package, Users } from "lucide-react";
import { adminApi } from "@/features/admin/api";

/**
 * Store analytics — reuses the existing admin dashboard aggregate endpoint.
 * Every admin role sees this page, but store_admin's data is store-scoped
 * server-side.
 */
export default function AdminAnalytics() {
    const { data, isLoading } = useQuery({
        queryKey: ["admin", "stats"],
        queryFn: adminApi.stats,
    });

    const cards = [
        {
            label: "Orders (all time)",
            value: data?.counts.orders ?? 0,
            icon: Package,
        },
        {
            label: "Revenue",
            value: `₹${(data?.revenue.total ?? 0).toLocaleString("en-IN")}`,
            icon: IndianRupee,
        },
        {
            label: "Customers",
            value: data?.counts.customers ?? 0,
            icon: Users,
        },
        {
            label: "Products",
            value: data?.counts.products ?? 0,
            icon: BarChart3,
        },
    ];

    return (
        <div data-testid="admin-analytics-page" className="space-y-6">
            <header>
                <h1 className="text-2xl font-extrabold">Analytics</h1>
                <p className="text-sm text-muted-foreground">
                    Store admins see figures for their own store only. Super/Ops see across all stores.
                </p>
            </header>

            {isLoading ? (
                <p>Loading…</p>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {cards.map((c) => (
                        <div key={c.label} className="rounded-2xl border border-border/60 bg-white p-5" data-testid={`analytics-card-${c.label.split(" ")[0].toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                                    {c.label}
                                </p>
                                <c.icon className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <p className="mt-3 text-3xl font-extrabold">{c.value}</p>
                        </div>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                For deeper analytics (charts, cohort retention, RFM), connect a BI tool to the MongoDB read-replica.
            </p>
        </div>
    );
}
